import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BehaviorSubject, type Observable, type Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { makeStyles } from 'tss-react/mui';

import { navDrawerWidth } from '~/resources';
import * as AuthService from '~/services/authentication';
import { AuthStatus, type Roles } from '~/types/types';

const useStyles = makeStyles()({
  drawer: {
    width: navDrawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: navDrawerWidth,
  },
  navHeader: {
    textAlign: 'right',
  },
});

interface DrawerMenuItem {
  text: string;
  route: string;
  acceptableRoles: Readonly<Roles[]>;
}

interface DrawerMenuData {
  items: (DrawerMenuItem | string)[];
}

interface PreprocessedDrawerMenuItem {
  item: DrawerMenuItem | string;
  visible: boolean;
}

interface NavDrawerProps {
  closeDrawer: () => void;
  drawerOpen: boolean;
  data: DrawerMenuData;
}

const NavDrawer = ({ closeDrawer, drawerOpen, data }: NavDrawerProps) => {
  const { classes } = useStyles();
  const [items, setItems] = useState<PreprocessedDrawerMenuItem[]>([]);
  let itemsSub: Subscription;

  useEffect(() => {
    const itemsObservable: Observable<PreprocessedDrawerMenuItem[]> = combineLatest(
      data.items.map((item) => {
        if (typeof item === 'string') {
          return new BehaviorSubject({ item, visible: true }).asObservable();
        }
        return AuthService.getAuthStatusObs(item.acceptableRoles).pipe(
          map((authStatus) => ({
            item,
            visible: authStatus === AuthStatus.Authorized,
          }))
        );
      })
    );

    itemsSub = itemsObservable.subscribe((newItems) => {
      setItems(newItems);
    });

    return () => {
      if (itemsSub) {
        itemsSub.unsubscribe();
      }
    };
  }, [data.items]);

  return (
    <Drawer
      className={classes.drawer}
      anchor="left"
      open={drawerOpen}
      variant="persistent"
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <div className={classes.navHeader}>
        <IconButton onClick={closeDrawer} size="large">
          <ChevronLeftIcon />
        </IconButton>
      </div>
      <Divider />
      <List>
        {items.map((item, i) => {
          if (!item.visible) {
            return null;
          }
          if (typeof item.item === 'string') {
            if (item.item === 'divider') {
              return <Divider variant="middle" key={i} />;
            }
            return null;
          } else {
            return (
              <Link to={item.item.route} key={i}>
                <ListItem onClick={closeDrawer}>
                  <ListItemText primary={item.item.text} />
                </ListItem>
              </Link>
            );
          }
        })}
      </List>
    </Drawer>
  );
};

export default NavDrawer;
